import { FC, useCallback, useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import logoLight from "../../assets/icons/logo_light.svg";
import logoDark from "../../assets/icons/logo.svg";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { ArrowRight, User } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Container from "../Container";
import CartDrawer from "@/components/UI/Buttons/Cart";
import { useCart } from "@/components/UI/Buttons/CartContext";
import ShoppingBasket from "@/assets/icons/shopping-basket";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/UI/Sheet/Sheet";
import { VisuallyHidden } from "radix-ui";
import ROUTES from "@/data/Routes";
import { getHomeHref } from "@/utils/homeHeroSession";
import ActionLink from "@/components/UI/Buttons/ActionLink";
import { useAuth } from "@/data/context/authContext";

const mobileNavLinks = [
  { id: 1, label: "Narystės", href: ROUTES.MEMBERSHIPS },
  {
    id: 2,
    label: "Sužinok daugiau",
    items: [
      { id: 21, label: "Apie mus", href: ROUTES.ABOUT },
      { id: 22, label: "Komanda", href: ROUTES.TEAM },
      { id: 23, label: "Tinklaraštis", href: ROUTES.BLOG },
      { id: 24, label: "D.U.K", href: ROUTES.FAQ },
      { id: 25, label: "Dovanų kuponas", href: ROUTES.GIFT_COUPON },
      { id: 26, label: "Kontaktai", href: ROUTES.CONTACTS },
    ],
  },
];

const MOB_MENU_CLOSE_DELAY_MS = 100;

type HeaderTheme = "transparent" | "light";

interface HeaderProps {
  theme?: HeaderTheme;
}

const Header: FC<HeaderProps> = ({ theme = "light" }) => {
  const location = useLocation();
  const [isOpenMobMenuSheet, setIsOpenMobMenuSheet] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const { cartItems } = useCart();
  const { user } = useAuth();

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const homeHref = getHomeHref(location.search);
  const accountHref = user === null ? ROUTES.LOGIN : ROUTES.ACCOUNT;
  const accountAriaLabel = user === null ? "Prisijungti" : "Paskyra";

  const closeMobMenuSoon = useCallback(() => {
    window.setTimeout(
      () => setIsOpenMobMenuSheet(false),
      MOB_MENU_CLOSE_DELAY_MS,
    );
  }, []);

  useEffect(() => {
    const id = window.setTimeout(
      () => setIsOpenMobMenuSheet(false),
      MOB_MENU_CLOSE_DELAY_MS,
    );
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setIsScrolled(currentScrollPos > 10);

      if (currentScrollPos > 10) {
        setVisible(prevScrollPos > currentScrollPos);
      } else {
        setVisible(true);
      }

      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  const activeTheme = isScrolled ? "light" : theme;

  const isTransparent = activeTheme === "transparent";

  const headerBgClass = isTransparent
    ? "bg-transparent backdrop-blur-md"
    : "bg-white shadow-sm";
  const textColorClass = isTransparent ? "text-white" : "text-dark";
  const textColorHoverClass = isTransparent
    ? "hover:text-white/80"
    : "hover:text-gray-600";
  const activeLogo = isTransparent ? logoLight : logoDark;
  const hamburgerColorClass = isTransparent ? "text-dark" : "text-dark";
  const hamburgerBgClass = isTransparent
    ? "bg-primary border-primary hover:bg-primary/90"
    : "bg-white border-[#EDEDED] hover:bg-gray-50";

  const cartBtnClassMobile =
    "relative rounded-full border border-[#EDEDED] bg-white p-1.5 text-dark transition-colors hover:bg-gray-50";
  const cartBtnClass = `relative isolate rounded-full border p-1.5 transition-all duration-300 ease-out hover:text-white before:pointer-events-none before:content-[''] before:absolute before:-inset-px before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-300 before:ease-out before:[background-image:var(--gradient-brand-light)] before:z-0 hover:before:opacity-100 ${
    isTransparent
      ? "border-white/60 bg-white hover:bg-white/90"
      : "border-ink bg-ink hover:bg-ink/90"
  }`;
  const cartIconColorMobile = "#181818";
  const cartIconColor = isTransparent ? "#181818" : "#FFFFFF";
  const cartBadgeClass = isTransparent
    ? "bg-ink text-paper"
    : "bg-paper text-ink border border-ink/10";

  const desktopDropdownBtnClass = isDesktopDropdownOpen
    ? `bg-white text-dark font-medium ${!isTransparent && "shadow-sm border border-gray-100"}`
    : `font-normal ${textColorClass} ${textColorHoverClass}`;

  return (
    <>
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <header
        className={`z-50 w-full transition-all duration-300 ${
          isScrolled ? "fixed top-0" : "fixed top-0 md:absolute"
        } ${headerBgClass} ${isScrolled && !visible ? "-translate-y-full" : "translate-y-0"}`}
      >
        <div className="flex items-center px-4 py-4 md:h-25 md:py-4">
          <Container>
            <nav className="flex w-full items-center justify-between gap-4">
              {/* Mobile */}
              <div className="flex w-full items-center justify-between md:hidden">
                <NavLink to={homeHref}>
                  <img
                    src={activeLogo}
                    className="w-[150px]"
                    alt="Telomera logo"
                  />
                </NavLink>

                <div className="flex items-center gap-3">
                  <Link
                    to={accountHref}
                    aria-label={accountAriaLabel}
                    className={cartBtnClassMobile}
                  >
                    <User
                      size={18}
                      className="relative z-10"
                      color={cartIconColorMobile}
                      strokeWidth={1.75}
                    />
                  </Link>

                  <Sheet
                    open={isOpenMobMenuSheet}
                    onOpenChange={setIsOpenMobMenuSheet}
                  >
                    <SheetTrigger asChild>
                      <button
                        type="button"
                        aria-label={
                          cartCount > 0
                            ? `Atidaryti meniu, krepšelyje ${cartCount} prek.`
                            : "Atidaryti meniu"
                        }
                        className={`relative rounded-lg border p-1.5 transition-colors ${hamburgerBgClass}`}
                      >
                        <Bars3Icon
                          className={`size-5 cursor-pointer ${hamburgerColorClass}`}
                        />
                        {cartCount > 0 && (
                          <span
                            className="bg-ink text-paper absolute -top-1 -right-1 z-10 grid min-h-5 min-w-5 place-items-center rounded-full px-1 text-[10px] leading-none font-semibold"
                            aria-hidden
                          >
                            {cartCount > 99 ? "99+" : cartCount}
                          </span>
                        )}
                      </button>
                    </SheetTrigger>
                    <SheetContent
                      side="right"
                      className="w-full overflow-y-auto border-none bg-white p-0 sm:w-[540px]"
                    >
                      <VisuallyHidden.Root>
                        <SheetHeader>
                          <SheetTitle>Menu</SheetTitle>
                          <SheetDescription></SheetDescription>
                        </SheetHeader>
                      </VisuallyHidden.Root>
                      <NavbarMob
                        cartCount={cartCount}
                        onCloseMenu={closeMobMenuSoon}
                        onOpenCart={() => {
                          closeMobMenuSoon();
                          setIsCartOpen(true);
                        }}
                      />
                    </SheetContent>
                  </Sheet>
                </div>
              </div>

              {/* Desktop */}
              <div className="hidden w-full items-center justify-between md:flex md:h-[59px]">
                <NavLink to={homeHref}>
                  <img
                    src={activeLogo}
                    className="h-10 w-50"
                    alt="Telomera logo"
                  />
                </NavLink>

                <div className="flex items-center gap-6">
                  <NavLink
                    to={ROUTES.MEMBERSHIPS}
                    className={`text-[18px] font-normal transition-colors ${textColorClass} ${textColorHoverClass}`}
                  >
                    Narystės
                  </NavLink>

                  <DropdownMenu.Root onOpenChange={setIsDesktopDropdownOpen}>
                    <DropdownMenu.Trigger asChild>
                      <button
                        className={`flex items-center gap-1.5 rounded-md px-5 py-2 text-[18px] transition-colors outline-none ${desktopDropdownBtnClass}`}
                      >
                        Sužinok daugiau
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${isDesktopDropdownOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                    </DropdownMenu.Trigger>

                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="start"
                        sideOffset={12}
                        className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2 z-[60] flex w-[260px] flex-col rounded-[16px] bg-white p-[8px] shadow-lg"
                      >
                        {/* the 2nd item has the dropdown links */}
                        {mobileNavLinks[1].items &&
                          Array.isArray(mobileNavLinks[1].items) &&
                          mobileNavLinks[1].items.map((item) => (
                            <DropdownMenu.Item key={item.id} asChild>
                              <Link
                                to={item.href}
                                className="text-dark cursor-pointer rounded-lg px-5 py-2.5 text-[18px] transition-colors outline-none hover:bg-[#F5F5F5]"
                              >
                                {item.label}
                              </Link>
                            </DropdownMenu.Item>
                          ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>

                <div className="flex items-center gap-3">
                  <Link
                    to={accountHref}
                    aria-label={accountAriaLabel}
                    className={cartBtnClass}
                  >
                    <User
                      size={35}
                      className="relative z-10"
                      color={cartIconColor}
                      strokeWidth={1.75}
                    />
                  </Link>

                  {cartCount > 0 && (
                    <button
                      type="button"
                      aria-label="Open cart"
                      className={cartBtnClass}
                      onClick={() => setIsCartOpen(true)}
                    >
                      <ShoppingBasket
                        width={35}
                        height={35}
                        className="relative z-10"
                        color={cartIconColor}
                      />
                      {cartCount > 0 && (
                        <span
                          className={`${cartBadgeClass} absolute -top-px -right-px z-10 grid min-w-5 place-items-center rounded-full px-1 text-[11px] leading-[18px]`}
                        >
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      )}
                    </button>
                  )}

                  <ActionLink
                    to={ROUTES.CONSULTATION}
                    variant={isTransparent ? "light" : "dark"}
                    hoverGradientLight
                    onHover
                    showIcon={false}
                    className="py-3.5 pl-4.5 font-medium whitespace-nowrap md:py-3.5"
                  >
                    Registruotis konsultacijai
                  </ActionLink>
                </div>
              </div>
            </nav>
          </Container>
        </div>
      </header>
    </>
  );
};

const NavbarMob = ({
  cartCount = 0,
  onOpenCart,
  onCloseMenu,
}: {
  cartCount?: number;
  onOpenCart?: () => void;
  onCloseMenu: () => void;
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <nav className="mt-[80px] flex w-full flex-col text-center text-[15px] font-normal text-[#181818]">
      <Link
        className="border-b border-[#EAEAEA] py-[22px] font-medium"
        to={ROUTES.CONSULTATION}
        onClick={onCloseMenu}
      >
        Registracija
      </Link>

      {cartCount > 0 && onOpenCart && (
        <button
          type="button"
          aria-label="Atidaryti krepšelį"
          className="relative flex w-full items-center justify-center gap-2 border-b border-[#EAEAEA] py-[22px] transition-colors hover:bg-[#F5F5F5]"
          onClick={onOpenCart}
        >
          <ShoppingBasket
            width={20}
            height={20}
            className="shrink-0"
            color="#181818"
          />
          <span className="font-medium">Krepšelis</span>
          <span className="bg-ink text-paper grid min-w-5 place-items-center rounded-full px-1.5 text-[11px] leading-[18px]">
            {cartCount > 99 ? "99+" : cartCount}
          </span>
        </button>
      )}

      {mobileNavLinks.map((item) => (
        <div key={item.id} className="flex w-full flex-col">
          {item.href ? (
            <Link
              className="border-b border-[#EAEAEA] py-[22px]"
              to={item.href}
              onClick={onCloseMenu}
            >
              {item.label}
            </Link>
          ) : (
            <>
              <button
                className={`flex items-center justify-center gap-[6px] border-b border-[#EAEAEA] py-[22px] ${expandedId === item.id ? "bg-[#EAEAEA]" : ""}`}
                onClick={() =>
                  setExpandedId(expandedId === item.id ? null : item.id)
                }
              >
                {item.label}
                <ChevronDownIcon
                  className={`h-3 w-3 transition-transform ${expandedId === item.id ? "rotate-180" : ""}`}
                />
              </button>
              {expandedId === item.id && (
                <div className="flex w-full flex-col bg-transparent">
                  {item.items &&
                    Array.isArray(item.items) &&
                    item.items.map(
                      (innerItem: {
                        id: number;
                        label: string;
                        href: string;
                      }) => (
                        <Link
                          key={innerItem.id}
                          className="border-b border-[#EAEAEA] py-[20px] text-[14px]"
                          to={innerItem.href}
                          onClick={onCloseMenu}
                        >
                          {innerItem.label}
                        </Link>
                      ),
                    )}
                </div>
              )}
            </>
          )}
        </div>
      ))}

      <div className="mt-12 flex justify-center pb-[80px]">
        <Link
          to={ROUTES.CONSULTATION}
          className="bg-dark flex items-center gap-2 rounded-full px-6 py-3.5 text-center text-[15px] font-medium text-white transition-opacity hover:opacity-90"
          onClick={onCloseMenu}
        >
          Registruotis konsultacijai
          <ArrowRight size={18} />
        </Link>
      </div>
    </nav>
  );
};

export default Header;
